import { DEFAULT_MONEY_SCALE, roundMoney, type MoneyScale } from '@shared/money';
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { SaleAlreadyVoidedError } from '../domain/saleErrors';
import { StockInsufficientError } from '../../inventory/stock/domain/stockErrors';
import { getSaleDetail } from './salesRepoDetail';

export function isDuplicateSalePaymentConstraintError(raw: string): boolean {
  const msg = (raw || '').toLowerCase();
  return (
    msg.includes('sale_payments_sale_id_method_id_idx') ||
    (msg.includes('duplicate key') && msg.includes('sale_payments'))
  );
}

async function recoverProcessSaleCommandAfterDuplicatePayment(
  saleId: string,
): Promise<ProcessSaleCommandResult | null> {
  const prior = await getSaleDetail(saleId).catch(() => null);
  if (!prior || prior.status === 'voided') return null;
  return {
    applied: true,
    counterNumber: prior.counterNumber ?? null,
    pickupName: prior.pickupName ?? null,
    fulfillmentType: prior.fulfillmentType ?? null,
  };
}

export type ProcessSaleCommandResult = {
  applied: true;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
  saleDraftStatus?: 'not_requested' | 'consumed';
  customerCreditStatus?: 'applied' | 'not_applicable';
  commissionStatus?: 'accrued' | 'not_applicable';
};

function roundSaleMoney(n: number, scale: MoneyScale = DEFAULT_MONEY_SCALE): number {
  return roundMoney(n, scale);
}

function discretionaryDiscountRows(discounts: unknown[]): unknown[] {
  return discounts.filter((d) => {
    if (!d || typeof d !== 'object') return false;
    const pid = (d as { promotionId?: unknown }).promotionId;
    return !(typeof pid === 'string' && pid.trim().length > 0);
  });
}

function sumDiscountRows(rows: unknown[]): number {
  return rows.reduce<number>((sum, d) => {
    if (!d || typeof d !== 'object') return sum;
    const amt = Number((d as { discountAmount?: unknown }).discountAmount);
    return sum + (Number.isFinite(amt) ? amt : 0);
  }, 0);
}

/** Alinea ítems y totales con el claim de evaluate-sale-promotions (ADR-032). */
export function mergeAuthoritativePromoClaimIntoPayload(
  commandPayload: Record<string, unknown>,
  claim: { claimId: string; discountTotal?: number; discounts?: unknown[] },
): void {
  commandPayload.promoClaimId = claim.claimId;
  if (typeof claim.discountTotal !== 'number' || !Number.isFinite(claim.discountTotal)) {
    return;
  }

  const items = Array.isArray(commandPayload.items) ? commandPayload.items : [];

  const gross = items.reduce<number>((sum, raw) => {
    const it = (raw ?? {}) as Record<string, unknown>;
    const qty = Number(it.quantity) || 0;
    const orig = Number(it.unitPriceOriginal ?? it.price) || 0;
    return sum + qty * orig;
  }, 0);

  const existingDiscounts = Array.isArray(commandPayload.discounts) ? commandPayload.discounts : [];
  const discretionary = discretionaryDiscountRows(existingDiscounts);
  const discretionaryTotal = sumDiscountRows(discretionary);
  const promoDiscount = claim.discountTotal;
  const tip = Math.max(0, Number(commandPayload.tipAmount ?? 0));

  const claimDiscounts = Array.isArray(claim.discounts) ? claim.discounts : [];

  commandPayload.discountTotal = roundSaleMoney(promoDiscount + discretionaryTotal);
  commandPayload.subtotal = roundSaleMoney(gross);
  commandPayload.total = roundSaleMoney(Math.max(0, gross - promoDiscount - discretionaryTotal + tip));
  commandPayload.discounts = [...claimDiscounts, ...discretionary];
}

/** Precio unitario neto en `price` — debe coincidir con sale_promo_cart_hash (antes del claim). */
export function normalizePromoHashItemPrices(items: unknown[]): void {
  if (!Array.isArray(items)) return;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as Record<string, unknown>;
    const qty = Number(it.quantity) || 0;
    const orig = Number(it.unitPriceOriginal ?? it.price) || 0;
    const disc = Number(it.discountAmount) || 0;
    if (qty <= 0 || orig <= 0 || disc <= 0) continue;
    it.price = roundSaleMoney(Math.max(0, orig * qty - disc) / qty);
  }
}

/** Reparte montos del claim por productId (lineId del edge = productId:variant:idx). */
export function syncSaleItemPromoFieldsFromClaim(
  items: unknown[],
  claimDiscounts: unknown[],
): void {
  if (!Array.isArray(items) || !Array.isArray(claimDiscounts)) return;
  const promoByProduct = new Map<string, number>();
  for (const d of claimDiscounts) {
    if (!d || typeof d !== 'object') continue;
    const row = d as Record<string, unknown>;
    const meta = row.metadata as Record<string, unknown> | undefined;
    const lineKey = typeof meta?.lineId === 'string' ? meta.lineId : '';
    const productId = lineKey.split(':')[0] ?? '';
    if (!productId) continue;
    const amt = Number(row.discountAmount);
    if (!Number.isFinite(amt)) continue;
    promoByProduct.set(productId, roundSaleMoney((promoByProduct.get(productId) ?? 0) + amt));
  }
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as Record<string, unknown>;
    const productId = String(it.productId ?? '');
    const disc = promoByProduct.get(productId);
    if (disc == null || disc <= 0) continue;
    const qty = Number(it.quantity) || 0;
    const orig = Number(it.unitPriceOriginal ?? it.price) || 0;
    if (qty <= 0 || orig <= 0) continue;
    it.discountAmount = disc;
    it.price = roundSaleMoney(Math.max(0, orig * qty - disc) / qty);
  }
}

export function buildProcessSaleCommandPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const saleId = typeof payload.saleId === 'string' ? payload.saleId.trim() : '';
  return {
    ...payload,
    operationId:
      typeof payload.operationId === 'string' && payload.operationId.trim()
        ? payload.operationId.trim()
        : saleId
          ? `${saleId}:process`
          : crypto.randomUUID(),
  };
}

export function mapProcessSaleRemoteFailure(raw: string): Error {
  const msg = (raw || '').trim();
  if (/sale_already_voided/i.test(msg)) {
    return new SaleAlreadyVoidedError();
  }
  if (/stock_policy_block/i.test(msg)) {
    return new StockInsufficientError(
      'No hay stock suficiente para completar la venta.',
    );
  }
  if (/promo_discount_mismatch/i.test(msg)) {
    return new Error(
      'La promo ya no coincide con el carrito (precio, cantidad o vigencia). Revisá el total y cobrá de nuevo.',
    );
  }
  if (/promo_usage_limit_reached/i.test(msg)) {
    return new Error('Se alcanzó el límite de usos de esa promo.');
  }
  if (/promo_not_applicable|promo_code_invalid/i.test(msg)) {
    return new Error('Esa promo no aplica a este carrito.');
  }
  if (/promo_stale|promo_claim_invalid/i.test(msg)) {
    return new Error('La promo se venció. Revisá el carrito e intentá de nuevo.');
  }
  if (/promo_permission_denied/i.test(msg)) {
    return new Error('Hace falta permiso o PIN para esa promo.');
  }
  if (/module_permission_denied.*discount|supervisor_override/i.test(msg)) {
    return new Error('Hace falta permiso o PIN para ese descuento.');
  }
  const menuOptMatch = msg.match(/sale_menu_option_invalid:\s*(.+)$/i);
  if (menuOptMatch?.[1]?.trim()) {
    return new Error(menuOptMatch[1].trim());
  }
  if (/package_customer_required/i.test(msg)) {
    return new Error('El paquete necesita un cliente. Elegilo arriba del cobro.');
  }
  if (/package_price_mismatch/i.test(msg)) {
    return new Error('El precio del paquete cambió. Sacalo del carrito y agregalo de nuevo.');
  }
  if (/package_quantity_invalid/i.test(msg)) {
    return new Error('La cantidad del paquete no es válida.');
  }
  if (/package_not_available|package_id_required/i.test(msg)) {
    return new Error('Ese paquete ya no está disponible en esta sucursal.');
  }
  if (/capability not enabled:?\s*service_packages/i.test(msg)) {
    return new Error('Los paquetes no están habilitados en este negocio.');
  }
  // Errores de negocio con mapping dedicado en showCheckoutError (checkout POS):
  // pasan crudos para que el handler traduzca `code: detalle` al copy correcto.
  if (/combo_not_available|sale_price_mismatch|sale_total_mismatch/i.test(msg)) {
    return new Error(msg);
  }
  // Nunca pasar texto/códigos internos del backend al usuario.
  return new Error('No se pudo confirmar la venta. Reintentá o pedile a soporte.');
}

export async function processSaleCommandRemote(
  payload: Record<string, unknown>,
): Promise<ProcessSaleCommandResult> {
  const commandPayload = buildProcessSaleCommandPayload(payload);

  const discounts = Array.isArray(commandPayload.discounts) ? commandPayload.discounts : [];
  const hasPromoDiscount = discounts.some(
    (d) =>
      d &&
      typeof d === 'object' &&
      typeof (d as { promotionId?: unknown }).promotionId === 'string' &&
      String((d as { promotionId: string }).promotionId).trim().length > 0,
  );

  if (hasPromoDiscount) {
    const items = Array.isArray(commandPayload.items) ? commandPayload.items : [];
    normalizePromoHashItemPrices(items);
    const promoIntent = (commandPayload.promoIntent ?? {}) as {
      promoCodes?: string[];
      selectedPromotionIds?: string[];
    };
    const promoIntentForHash = {
      promoCodes: promoIntent.promoCodes ?? [],
      selectedPromotionIds: promoIntent.selectedPromotionIds ?? [],
    };
    // Mismo promoIntent en el RPC que en el hash del claim (auto-promos sin promoIntent en intake).
    commandPayload.promoIntent = promoIntentForHash;
    const hashPayload = {
      items: items.map((it) => {
        const row = (it ?? {}) as Record<string, unknown>;
        return {
          productId: row.productId,
          variantId: row.variantId ?? null,
          quantity: row.quantity,
          price: row.price,
        };
      }),
      promoIntent: promoIntentForHash,
    };
    // Evaluación: base PRE-promo (unitPriceOriginal), no el neto de línea.
    // hashPayload queda intacto para preservar la paridad de sale_promo_cart_hash.
    const evalItems = hashPayload.items.map((it, idx) => {
      const row = (items[idx] ?? {}) as Record<string, unknown>;
      return {
        ...it,
        unitPriceOriginal: row.unitPriceOriginal ?? it.price,
        categoryId: row.categoryId ?? null,
        brandId: row.brandId ?? null,
      };
    });
    const { data: claimData, error: claimError } = await supabase.functions.invoke(
      'evaluate-sale-promotions',
      {
        body: {
          branchId: commandPayload.branchId,
          items: evalItems,
          promoIntent: hashPayload.promoIntent,
          hashPayload,
        },
      },
    );
    if (claimError || !claimData || typeof claimData !== 'object' || !(claimData as { claimId?: string }).claimId) {
      const failure = new Error('No se pudo validar la promo. Reintentá.');
      captureOpsFailure({
        module: 'pos',
        op: 'pos.sale.promo-claim',
        error: failure,
        ingestError: claimError?.message ?? 'promo_claim_failed',
        applied: false,
        path: 'web-ram',
        itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
        extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
      });
      throw failure;
    }
    mergeAuthoritativePromoClaimIntoPayload(
      commandPayload,
      claimData as { claimId: string; discountTotal?: number; discounts?: unknown[] },
    );
  }

  const { data, error } = await supabase.schema('pos').rpc('process_sale_command', {
    p_payload: commandPayload,
  });
  if (error) {
    const saleId = typeof payload.saleId === 'string' ? payload.saleId.trim() : '';
    if (saleId && isDuplicateSalePaymentConstraintError(error.message || '')) {
      const recovered = await recoverProcessSaleCommandAfterDuplicatePayment(saleId);
      if (recovered) return recovered;
    }

    const failure = mapProcessSaleRemoteFailure(error.message || '');
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale.process-command',
      error: failure,
      ingestError: error.message || failure.message,
      applied: false,
      path: 'web-ram',
      itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
      extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
    });
    throw failure;
  }

  const result = (data ?? {}) as {
    ok?: boolean;
    counter_number?: number | null;
    pickup_name?: string | null;
    fulfillment_type?: string | null;
    commission_result?: { ok?: boolean };
    sale_draft_status?: 'not_requested' | 'consumed';
    customer_credit_status?: 'applied' | 'not_applicable';
    commission_status?: 'accrued' | 'not_applicable';
  };
  if (result.ok !== true) {
    const failure = new Error('El servidor no confirmó la venta.');
    captureOpsFailure({
      module: 'pos',
      op: 'pos.sale.process-command',
      error: failure,
      ingestError: 'sale_command_not_applied',
      applied: false,
      path: 'web-ram',
      itemId: typeof payload.saleId === 'string' ? payload.saleId : undefined,
      extra: { branchId: typeof payload.branchId === 'string' ? payload.branchId : null },
    });
    throw failure;
  }

  return {
    applied: true,
    counterNumber: result.counter_number ?? null,
    pickupName: result.pickup_name ?? null,
    fulfillmentType: result.fulfillment_type ?? null,
    saleDraftStatus: result.sale_draft_status,
    customerCreditStatus: result.customer_credit_status,
    commissionStatus: result.commission_status,
  };
}
